import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { appConfig } from 'src/config';
import { CampaignStats, CDRStatus } from './portal';
import { withSessionExpiredHandler } from 'src/api/common/baseQueryWithAuth';
import { CampaignLogStatus } from './campaigns';

/**
 * V2 Viber statistics live under `{host}/viber/v2/api/...`, while the RTK base URL
 * (`VIBER_URL`) already ends in `.../viber/api/`. Appending `v2/api/...` to the base
 * would produce a malformed `.../viber/api/v2/api/...` path (404), so we build an
 * absolute URL off the host root instead (fetchBaseQuery skips the base for absolute URLs).
 */
const VIBER_V2_API_BASE = `${(appConfig.VIBER_URL ?? '').replace(/\/?api\/?$/, '/')}v2/api/`;

export enum ViberTemplateStatus {
  new = 'new',
  approved = 'approved',
  rejected = 'rejected',
  readyForApproval = 'readyForApproval',
  removedByViber = 'removedByViber',
}

export interface ViberTemplate {
  accountId: string;
  id: string;
  approvalAt: string | null;
  info: string | null;
  name: string;
  originalText: string;
  translateText: string;
  status: ViberTemplateStatus;
}

export interface ViberTemplateCreateRequest {
  name: string;
  originalText: string;
  translateText: string;
  accountId?: string;
  tenantId?: string;
  status?: ViberTemplateStatus;
  approvalAt?: string | null;
  info?: string | null;
}

export interface ViberAccount {
  id: string;
  name: string;
  serviceId: number;
  numberFilteringMode: string;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  appId: string;
}

export type ViberPricingPeriodCategory = 'FUTURE' | 'ACTIVE' | 'CURRENT' | 'PAST';

export const isActiveViberPricingPeriod = (periodCategory?: ViberPricingPeriodCategory | null) =>
  periodCategory === 'ACTIVE' || periodCategory === 'CURRENT';

/** OpenAPI: PricingData from GET pricing/{accountId} */
export interface ViberPricingData {
  id: number;
  country: { name: string; callingCode: string };
  accountId: string;
  promotionalRate: number;
  transactionalRate: number;
  otpRate?: number | null;
  sessionsRate?: number | null;
  internationalRate?: number | null;
  monthlyFee?: number | null;
  startDate?: string;
  endDate?: string | null;
  periodCategory: ViberPricingPeriodCategory;
}

export interface ViberPricing {
  id: number;
  countryCode: string;
  countryName: string;
  transactionalPrice: number;
  promotionalPrice: number;
  templateOtpPrice?: number | null;
  sessionsChatBotPrice?: number | null;
  internationalPrice?: number | null;
  monthlyFee?: number | null;
  startDate?: string;
  periodCategory?: ViberPricingPeriodCategory;
}

/** OpenAPI: MonthlyFeeData from GET monthly-fee/{accountId}/customer */
export interface ViberMonthlyFeeData {
  id?: number;
  accountId?: string;
  monthlyFee?: number;
}

export interface ViberMonthlyFee {
  fee: number | null;
}

export interface CampaignLogButton {
  action: string | null;
  caption: string | null;
}

export interface CampaignLog {
  id: string;
  createdAt: string;
  updatedAt: string;
  txt: string;
  message?: {
    txt?: string;
  };
  phoneNumber: string;
  trackingData: string;
  mediaURL: string | null;
  thumbnail: string | null;
  fileName: string | null;
  button: CampaignLogButton;
  messageType: string;
  status: CampaignLogStatus;
  statusDetails: string;
  messageDirection: string;
  fallbackStatus?: CampaignLogStatus;
  fallbackStatusDetails?: string;
}

export interface CampaignLogsResponse {
  content: CampaignLog[];
  page?: {
    number: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
  numberOfElements?: number;
  last?: boolean;
  first?: boolean;
  empty?: boolean;
}

export const mapViberPricingData = (item: ViberPricingData): ViberPricing => ({
  id: item.id,
  countryCode: item.country.callingCode,
  countryName: item.country.name,
  transactionalPrice: item.transactionalRate,
  promotionalPrice: item.promotionalRate,
  templateOtpPrice: item.otpRate ?? null,
  sessionsChatBotPrice: item.sessionsRate ?? null,
  internationalPrice: item.internationalRate ?? null,
  monthlyFee: item.monthlyFee ?? null,
  startDate: item.startDate,
  periodCategory: item.periodCategory,
});

/** Legacy price-list row shape (pre pricing/{accountId} API). */
export interface LegacyViberPriceListItem {
  id: number;
  countryCode: string;
  countryName: string;
  transactionalPrice: number;
  promotionalPrice: number;
  templateOtpPrice?: number | null;
  sessionsChatBotPrice?: number | null;
  internationalPrice?: number | null;
}

export const mapLegacyViberPriceListItem = (item: LegacyViberPriceListItem): ViberPricing => ({
  id: item.id,
  countryCode: item.countryCode,
  countryName: item.countryName,
  transactionalPrice: item.transactionalPrice,
  promotionalPrice: item.promotionalPrice,
  templateOtpPrice: item.templateOtpPrice ?? null,
  sessionsChatBotPrice: item.sessionsChatBotPrice ?? null,
  internationalPrice: item.internationalPrice ?? null,
});

/** Swagger: viber-pricing-controller — GET /api/pricing/{accountId} */
export const viberPricingPath = (accountId: string) => `pricing/${accountId}`;

/** Swagger: viber-monthly-fee-controller — GET /api/monthly-fee/{accountId}/customer */
export const viberMonthlyFeeCustomerPath = (accountId: string) => `monthly-fee/${accountId}/customer`;

const isPricingDataRow = (item: Record<string, unknown>) =>
  Boolean(item.country && typeof item.country === 'object');

export const normalizeViberPricingResponse = (response: unknown): ViberPricing[] => {
  const list = Array.isArray(response) ? response : response ? [response] : [];
  if (!list.length) return [];

  const first = list[0] as Record<string, unknown>;
  if (isPricingDataRow(first)) {
    const pricingList = list as ViberPricingData[];
    return pricingList.filter((item) => isActiveViberPricingPeriod(item.periodCategory)).map(mapViberPricingData);
  }

  return (list as LegacyViberPriceListItem[]).map(mapLegacyViberPriceListItem);
};

const normalizeViberMonthlyFeeResponse = (
  response: unknown,
  accountId: string
): ViberMonthlyFee => {
  const list = Array.isArray(response)
    ? (response as ViberMonthlyFeeData[])
    : response
      ? [response as ViberMonthlyFeeData]
      : [];
  const match = list.find((item) => item.accountId === accountId) ?? list[0];
  return { fee: match?.monthlyFee ?? null };
};

export interface ViberBalanceOverview {
  allMessagesCount: number;
  balance: number;
  dailyStats: { [key: string]: number };
  promotionalMessagesCount: number;
  revenue: number;
  totalUsage: number;
  transactionalMessagesCount: number;
}

export interface ViberMessageTypeData {
  amount: number;
  unitCost: number;
}

export interface ViberCountryDayData {
  types: Record<string, ViberMessageTypeData>;
}

export interface ViberDayData {
  countries: Record<string, ViberCountryDayData>;
}

export interface ViberAccountDailyStats {
  dailyStats: Record<string, ViberDayData>;
}

export interface ViberStatisticPerAccountData {
  perAccount: Record<string, ViberAccountDailyStats>;
}

/** Result of verifying campaign recipients against the Viber ID pricelist (mirrors WhatsApp). */
export interface ViberContactsWithoutPricing {
  /** Number of recipients whose destination country has no active Viber pricing. */
  phoneNumbers: number;
  /** Unsupported destination countries for the selected Viber ID. */
  countries: string[];
}

export interface ViberMonthlyBalanceOverview {
  monthlyCount?: Record<number, number>;
}

export interface ViberTenantConfig {
  featureFlags: {
    ACTIVE: boolean;
    CAMPAIGN: boolean;
    ONE_TO_ONE: boolean;
  };
  campaign?: {
    messageCategories?: string[];
    messageTypes?: string[];
  };
}

export interface CDR {
  receiver: string;
  account: number;
  rate: null | number;
  statusDetails: null | string;
  createDateTime: string;
  sender: string;
  destinationName: null | string;
  lastStatusTime: null | string;
  lastStatus: null | CDRStatus;
  category?: null | string;
  messageType?: null | string;
  messageRateType?: null | string;
}

export type ViberInvoiceStatus =
  | 'DRAFT'
  | 'OPEN'
  | 'PAID'
  | 'UNPAID'
  | 'OVERDUE'
  | 'IN_PROGRESS'
  | 'ERROR'
  | 'SENT'
  | 'NOT_SENT'
  | 'DELETED';

export interface ViberInvoice {
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: ViberInvoiceStatus;
  customerId?: string;
  customerName: string;
  issueDate: string;
  saleDate?: string;
  dueDate: string;
  periodFrom: string;
  periodTo: string;
  deleted: boolean;
  errors?: string;
  pdfUrl?: string;
  excelUrl?: string;
}

export interface ViberInvoicesPage {
  content: ViberInvoice[];
  page: { number: number; size: number; totalElements: number; totalPages: number };
  last: boolean;
}

/** POST /tenants/{tenantId}/accounts (OpenAPI: ViberAccountRequestData). */
export interface ViberAccountCreateRequest {
  name: string;
  serviceId: number;
  appId: string;
  disabled: boolean;
  numberFilteringMode?: string;
}

export const viberApi = createApi({
  tagTypes: [
    'ViberTemplate',
    'ViberAccount',
    'ViberPricing',
    'ViberBalanceOverview',
    'ViberMonthlyStats',
    'ViberConfig',
    'CDR',
    'ViberInvoice',
    'ViberContactsWithoutPricing',
  ],
  baseQuery: withSessionExpiredHandler(fetchBaseQuery({
    baseUrl: `${appConfig.VIBER_URL}`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('chatToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  })),
  reducerPath: 'viberApi',
  endpoints: (builder) => ({
    getAccounts: builder.query<ViberAccount[], string>({
      query: (tenantId) => ({
        url: `tenants/${tenantId}/accounts`,
      }),
      providesTags: [{ type: 'ViberAccount', id: 'accounts' }],
      transformResponse: ({ content }: { content: ViberAccount[] }) => content,
    }),
    getTemplates: builder.query<ViberTemplate[], string>({
      query: (id) => ({
        url: `tenants/${id}/templates`,
      }),
      providesTags: [{ type: 'ViberTemplate', id: 'templates' }],
    }),
    getConfig: builder.query<ViberTenantConfig, string>({
      query: (tenantId) => ({
        url: `tenants/${tenantId}/config`,
      }),
      providesTags: [{ type: 'ViberConfig', id: 'config' }],
    }),
    getPricing: builder.query<ViberPricing[], string>({
      query: (accountId) => viberPricingPath(accountId),
      transformResponse: (response: unknown) => normalizeViberPricingResponse(response),
      providesTags: (_result, _error, accountId) => [{ type: 'ViberPricing', id: accountId }],
    }),
    getMonthlyFee: builder.query<ViberMonthlyFee, string>({
      query: (accountId) => viberMonthlyFeeCustomerPath(accountId),
      transformResponse: (response: unknown, _meta, accountId) =>
        normalizeViberMonthlyFeeResponse(response, accountId),
      providesTags: (_result, _error, accountId) => [{ type: 'ViberPricing', id: `monthly-fee-${accountId}` }],
    }),
    getInvoices: builder.query<
      ViberInvoicesPage,
      { tenantId: string; page: number; size: number; sort: string }
    >({
      query: ({ tenantId, page, size, sort }) => ({
        url: `invoices/${tenantId}/`,
        params: { page, size, sort },
      }),
      providesTags: [{ type: 'ViberInvoice', id: 'list' }],
      transformResponse: (response: unknown) => {
        const raw = response as Record<string, unknown>;
        const content = (Array.isArray(raw?.content) ? raw.content : []) as ViberInvoice[];
        const number = (raw?.number as number) ?? 0;
        const size = (raw?.size as number) ?? 20;
        const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
        const last = raw?.last === true;
        const totalElements =
          typeof raw?.totalElements === 'number'
            ? (raw.totalElements as number)
            : last
              ? number * size + numberOfElements
              : (number + 2) * size;

        return {
          content,
          last,
          page: {
            number,
            size,
            totalElements,
            totalPages: typeof raw?.totalPages === 'number' ? (raw.totalPages as number) : last ? number + 1 : number + 2,
          },
        };
      },
    }),
    getUserBalanceOverview: builder.query<ViberBalanceOverview, { id: string; dateFrom: string; dateTo: string }>({
      query: ({ id, dateFrom, dateTo }) => ({
        url: `${id}/statistics/user-balance-overview`,
        params: { from: dateFrom, to: dateTo },
      }),
      providesTags: [{ type: 'ViberBalanceOverview', id: 'balanceOverview' }],
    }),
    getViberMonthlyStats: builder.query<
      ViberMonthlyBalanceOverview,
      { tenantId: string; year: number; appId?: string }
    >({
      query: ({ tenantId, year, appId }) => ({
        url: appId ? 'statistics/user-monthly-count' : `${tenantId}/statistics/user-monthly-count`,
        params: { year, ...(appId ? { appId } : {}) },
      }),
      providesTags: [{ type: 'ViberMonthlyStats', id: 'monthly' }],
    }),
    getViberTrafficOverview: builder.query<
      ViberStatisticPerAccountData,
      { tenantId?: string; dateFrom: string; dateTo: string; appId?: string }
    >({
      query: ({ tenantId, dateFrom, dateTo, appId }) => ({
        url: appId ? `${VIBER_V2_API_BASE}statistics/user-balance-overview` : `${tenantId}/statistics/user-balance-overview`,
        params: { from: dateFrom, to: dateTo, ...(appId ? { appId } : {}) },
      }),
      providesTags: [{ type: 'ViberBalanceOverview', id: 'trafficOverview' }],
    }),
    getViberBalanceOverviewV2: builder.query<
      ViberStatisticPerAccountData,
      { tenantId: string; dateFrom: string; dateTo: string }
    >({
      query: ({ tenantId, dateFrom, dateTo }) => ({
        url: `${VIBER_V2_API_BASE}${tenantId}/statistics/user-balance-overview`,
        params: { from: dateFrom, to: dateTo },
      }),
      providesTags: [{ type: 'ViberBalanceOverview', id: 'balanceOverviewV2' }],
    }),
    getCDRs: builder.query<
      { content: CDR[]; page: { number: number; size: number; totalElements: number; totalPages: number } },
      {
        id: string;
        page: number;
        size: number;
        sort: string;
        creationDateAfter?: string;
        creationDateBefore?: string;
        lastStatusTimeAfter?: string;
        lastStatusTimeBefore?: string;
        sender?: string;
        receiver?: string;
        destination?: string;
        account?: number;
        lastStatus?: string;
      }
    >({
      query: ({ id, ...params }) => ({
        url: `cdr/${id}`,
        params,
      }),
      providesTags: [{ type: 'CDR', id: 'cdrs' }],
      transformResponse: (response: unknown) => {
        const raw = response as Record<string, unknown>;
        const content = Array.isArray(raw?.content) ? raw.content : [];
        const number = (raw?.number as number) ?? 0;
        const size = (raw?.size as number) ?? 20;
        const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
        const last = raw?.last === true;
        const pageObj = raw?.page as Record<string, unknown> | undefined;
        const totalElementsFromApi =
          (raw?.totalElements as number) ?? (pageObj?.totalElements as number);
        const totalElements =
          typeof totalElementsFromApi === 'number'
            ? totalElementsFromApi
            : last
              ? number * size + numberOfElements
              : (number + 2) * size;
        const totalPagesFromApi = (raw?.totalPages as number) ?? (pageObj?.totalPages as number);
        const totalPages =
          typeof totalPagesFromApi === 'number'
            ? totalPagesFromApi
            : last
              ? number + 1
              : number + 2;
        return {
          content,
          page: {
            number,
            size,
            totalElements,
            totalPages,
          },
        };
      },
    }),
    getViberContactsWithoutPricing: builder.query<
      ViberContactsWithoutPricing,
      { accountId: string; contacts: { mobilePhone: string }[] }
    >({
      query: ({ accountId, contacts }) => ({
        url: `pricing/${accountId}/verification`,
        method: 'POST',
        body: { contacts },
      }),
      providesTags: [{ type: 'ViberContactsWithoutPricing', id: 'verification' }],
    }),
    createAccount: builder.mutation<void, { tenantId: string; body: ViberAccountCreateRequest }>({
      query: ({ tenantId, body }) => ({
        url: `tenants/${tenantId}/accounts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ViberAccount', id: 'accounts' }],
    }),
    createTemplate: builder.mutation<void, { tenantId: string; accountId: string; body: ViberTemplateCreateRequest }>({
      query: ({ tenantId, accountId, body }) => ({
        url: `tenants/${tenantId}/accounts/${accountId}/templates`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ViberTemplate', id: 'templates' }],
    }),
    getCampaignStats: builder.query<CampaignStats, { campaignId: number, accountUuid: string }>({
      query: ({ campaignId, accountUuid }) => ({
        url: `campaigns/${campaignId}/statistics`,
        params: { accountUuid : accountUuid  },
      }),
    }),
    getCampaignLogs: builder.query<
          CampaignLogsResponse,
          {
            page: number;
            size: number;
            sort: string;
            campaignId: number;
            accountUuid?: string;
            hasFallback?: boolean;
          }
        >({
          query: ({ campaignId, hasFallback, ...params }) => ({
            url: `campaigns/${campaignId}/logs${hasFallback ? '/fallback' : ''}`,
            params,
          }),
          transformResponse: (response: unknown): CampaignLogsResponse => {
            const raw = response as Record<string, unknown>;
            const content = Array.isArray(raw?.content) ? (raw.content as CampaignLog[]) : [];
            const number = (raw?.number as number) ?? 0;
            const size = (raw?.size as number) ?? 20;
            const numberOfElements = (raw?.numberOfElements as number) ?? content.length;
            const last = raw?.last === true;
            const pageObj = raw?.page as Record<string, unknown> | undefined;
            const totalElementsFromApi = (raw?.totalElements as number) ?? (pageObj?.totalElements as number);
            const totalElements =
              typeof totalElementsFromApi === 'number'
                ? totalElementsFromApi
                : last
                  ? number * size + numberOfElements
                  : (number + 2) * size;
            const totalPagesFromApi = (raw?.totalPages as number) ?? (pageObj?.totalPages as number);
            const totalPages =
              typeof totalPagesFromApi === 'number'
                ? totalPagesFromApi
                : last
                  ? number + 1
                  : number + 2;
    
            return {
              content,
              page: {
                number,
                size,
                totalElements,
                totalPages,
              },
              totalElements,
              totalPages,
              number,
              size,
              numberOfElements,
              last,
              first: raw?.first === true,
              empty: raw?.empty === true,
            };
          },
        }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetConfigQuery,
  useGetAccountsQuery,
  useGetPricingQuery,
  useGetMonthlyFeeQuery,
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetUserBalanceOverviewQuery,
  useGetViberMonthlyStatsQuery,
  useGetViberTrafficOverviewQuery,
  useGetViberBalanceOverviewV2Query,
  useLazyGetCDRsQuery,
  useCreateAccountMutation,
  useCreateTemplateMutation,
  useGetCampaignStatsQuery,
  useLazyGetCampaignLogsQuery,
  useLazyGetViberContactsWithoutPricingQuery,
  useGetViberContactsWithoutPricingQuery,
} = viberApi;
